import React from 'react'
import styled from 'styled-components'
import {
  slideInBottom,
  slideInLeft,
  slideInRight,
  slideInTop,
  slideOutBottom,
  slideOutLeft,
  slideOutRight,
  slideOutTop,
} from '../constants'
import { Flex } from '../Flex'
import { Typography } from '../Typography'
import { Icon, type IconType } from '../Icon'
import { Loading } from '../Loading'

export type ToastPlacement = 'topLeft' | 'topRight' | 'bottomLeft' | 'bottomRight' | 'top' | 'bottom'
export type ToastType = 'success' | 'info' | 'warning' | 'danger' | 'primary' | 'loading'

export interface ToastProps {
  toastKey?: string
  type: ToastType
  duration?: number // in milliseconds
  placement?: ToastPlacement
  message: string | React.ReactNode | ((remainingDuration: number) => React.ReactNode)
  closeable?: boolean
  onClose?: () => void
  extra?: React.ReactNode
  icon?: IconType | false;
  interval?: number; // Optionally update the message after a certain interval
  zIndex?: number;
}

interface ToastComponentProps extends ToastProps {
  removeToast: (key: string) => void
}

export default function Toast({
  toastKey = '',
  type,
  duration = 3000,
  message,
  placement = 'top',
  removeToast,
  closeable,
  onClose,
  extra,
  icon,
  interval,
}: ToastComponentProps) {
  const removalTimer = React.useRef<NodeJS.Timeout>()
  const intervalTimer = React.useRef<NodeJS.Timeout | null>(null)
  const [isLeaving, setIsLeaving] = React.useState(false)
  const [remainingDuration, setRemainingDuration] = React.useState(duration)
  const [currentMessage, setCurrentMessage] = React.useState<React.ReactNode>(
    typeof message === 'function' ? message(duration) : message
  )

  // Effect to handle toast removal based on duration
  React.useEffect(() => {
    let timer: NodeJS.Timeout
    if (duration > 0) {
      timer = setTimeout(triggerClose, duration)
    }
    return () => {
      if (timer) clearTimeout(timer)
      if (removalTimer.current) clearTimeout(removalTimer.current)
      if (intervalTimer.current) clearInterval(intervalTimer.current)
    }
  }, [duration])

  // Effect to update remaining time every second and update message
  React.useEffect(() => {
    const startTime = Date.now()
    if (interval && typeof message === 'function') {
      intervalTimer.current = setInterval(() => {
        const elapsedTime = Date.now() - startTime
        const timeLeft = Math.max(0, duration - elapsedTime)
        setRemainingDuration(timeLeft)
        setCurrentMessage(message(timeLeft))
      }, interval)
    }
    return () => {
      if (intervalTimer.current) clearInterval(intervalTimer.current)
    }
  }, [interval, message, duration])

  // Update when message prop or remaining duration changes
  React.useEffect(() => {
    if (typeof message === 'function') {
      setCurrentMessage(message(remainingDuration))
    } else {
      setCurrentMessage(message)
    }
  }, [message, remainingDuration]) 

  const triggerClose = () => {
    setIsLeaving(true)
    removalTimer.current = setTimeout(() => removeToast(toastKey), 300)
    onClose?.()
  }

  const showExtra = closeable || extra
  const realIcon = icon || getIconByType(type)

  return (
    <ToastWrapper type={type} placement={placement} isLeaving={isLeaving} data-testid={`toast-${toastKey}`}>
      <Flex justify="space-between" gap={16}>
        <Flex gap={16}>
          {realIcon !== false && <Icon height={24} width={24} type={realIcon} color="var(--text-primary-invert)" />}
          <DefaultText>{currentMessage}</DefaultText> 
        </Flex>
        {showExtra && (
          <Flex gap={16}>
            {extra}
            {closeable && (
              <Icon
                height={24}
                width={24}
                type="CLOSE_X"
                color="var(--text-primary-invert)"
                onClick={triggerClose}
                css={'cursor: pointer;'}
              />
            )}
          </Flex>
        )}
      </Flex>
    </ToastWrapper>
  )
} 

const ToastWrapper = styled.div<{ type: ToastType; placement: ToastPlacement; isLeaving: boolean; }>`
  pointer-events: all;
  padding: var(--radius-md, 16px) 32px;
  align-items: center;
  gap: var(--radius-md, 16px);
  border-radius: var(--radius-xl, 100px);

  background-color: ${(props) => getBackgroundColor(props.type)};
  margin-bottom: 8px;
  opacity: 1;

  transform: translateY(0);
  transition: transform 0.3s ease-out, opacity 0.3s ease-out;

  animation: ${(props) =>
      props.isLeaving
        ? getSlideOutAnimation(props.placement)
        : getSlideInAnimation(props.placement)}
    0.31s ease-out;
  @prefers-reduced-motion: reduce {
    animation: none;
  }
`

const DefaultText = styled(Typography)`
  color: var(--text-primary-invert);
  font-size: 16px;
  font-style: normal;
  font-weight: 700;
  line-height: 24px;
  letter-spacing: 0.4px;
`

const getIconByType = (type: ToastType): IconType | false => {
  switch (type) {
    case 'success':
      return 'SUCCESS_TOAST'
    case 'info':
      return 'INFO_TOAST'
    case 'warning':
      return 'WARNING_TOAST'
    case 'danger':
      return 'DANGER_TOAST'
    case 'primary':
      return false
    case 'loading':
      return 'LOADING'
  }
}

function getBackgroundColor(type: ToastType) {
  switch (type) {
    case 'success':
    case 'info':
    case 'warning':
    case 'danger':
    case 'primary':
      return `var(--${type})`
    case 'loading':
      return 'var(--info)'
  }
}

export const getSlideInAnimation = (placement: ToastPlacement) => {
  switch (placement) {
    case 'topLeft':
    case 'topRight':
    case 'top':
      return slideInTop
    case 'bottom':
      return slideInBottom
    case 'bottomLeft':
      return slideInLeft
    case 'bottomRight':
      return slideInRight
    default:
      return slideInTop
  }
}

export const getSlideOutAnimation = (placement: ToastPlacement) => {
  switch (placement) {
    case 'topLeft':
    case 'topRight':
    case 'top':
      return slideOutTop
    case 'bottom':
      return slideOutBottom
    case 'bottomLeft':
      return slideOutLeft
    case 'bottomRight':
      return slideOutRight
    default:
      return slideOutTop
  }
}
